import { events } from '@nykaa/data-layer/constants';
import { pushEvent } from '@nykaa/data-layer/utils';
import { variantTypes } from 'shared/constants';
import { TagObject } from '@nykaa/product-card/types';
import { isValidObject } from '@nykaa/utils/validations';
import { localStorageService } from 'service/storage';
import { RECENTTY_VIEWED_PRODUCTS } from 'constants/productPage';
import { logger } from '@nykaa/logger';
import {
  PrimaryCategories,
  RatingsVisibility,
  Variant,
  ProductNudges,
} from 'store/product/types';
import { useLocation } from 'react-router-dom';
import { getPageType } from 'components/Header/helper';
import PageTypes from 'constants/pageTypes';
import { isValidArrayLength } from '@nykaa/utils/validations';

export const isConfigurable = (type: string) =>
  type &&
  type.toUpperCase() !== variantTypes.SIMPLE &&
  type.toUpperCase() !== variantTypes.BUNDLE;

export const pushVideoPlay = (
  productId: string,
  positionInCarousel: number
) => {
  pushEvent(events.PRODUCT_VIDEO_PLAY, {
    productVideoPlay: `${productId}:${positionInCarousel}`,
  });
};

export const getTagObjects = (tags: string[], messages = {}): TagObject[] =>
  tags.map((tag) => {
    const tagStringID = `${tag.toUpperCase()}_TAG`;
    const translatedName = messages[tagStringID] || tag;
    return {
      id: tag,
      name: translatedName,
    };
  });

const filterAndSetProductAsViewed = (
  cachedList: string[],
  productID: string
): string[] => {
  if (isNaN(+productID)) {
    return cachedList;
  }
  const productCount = cachedList.length;
  const modifiedViewedList = [...cachedList];
  if (!productCount) {
    modifiedViewedList?.push(productID);
    return modifiedViewedList;
  }
  // Check if productID is already available in recently Viewed List
  const productIndex = cachedList.findIndex((product) => product === productID);
  if (productIndex >= 0) {
    modifiedViewedList?.splice(productIndex, 1);
  }
  modifiedViewedList.push(productID);
  if (modifiedViewedList.length > 20) {
    modifiedViewedList?.shift();
  }
  return modifiedViewedList;
};

export const getRecenltyViewedProductsFromLocal = (): string[] => {
  const recentyViewedItems = localStorageService.getItem(
    RECENTTY_VIEWED_PRODUCTS
  );
  let recentlyViewList = [];

  if (recentyViewedItems) {
    try {
      recentlyViewList = JSON.parse(recentyViewedItems);
    } catch (err) {
      logger.error(
        err,
        'Error while parsing recently view items from local storage'
      );
    }
  }
  return recentlyViewList;
};

export const setProductAsRecentlyViewed = (productID: string) => {
  const recentlyViewList = getRecenltyViewedProductsFromLocal();
  localStorageService.setItem(
    RECENTTY_VIEWED_PRODUCTS,
    JSON.stringify(filterAndSetProductAsViewed(recentlyViewList, productID))
  );
};

export const checkCategoryEligibility = (
  categories: string[],
  primaryCategories: PrimaryCategories
) => {
  if (!primaryCategories) {
    return false;
  }

  const { l1, l2, l3 } = primaryCategories;

  const primaryCategoryIds = [l1?.id, l2?.id, l3?.id];
  return primaryCategoryIds.some((id) => categories.includes(id));
};

export const checkRatingsVisibility = (
  rating: number,
  ratingCount: number,
  ratingsVisibility: RatingsVisibility
) => {
  const { ratingsCountLimit = 0, avgRatingValueLimit = 0 } =
    ratingsVisibility || {};
  if (!(ratingCount < ratingsCountLimit && rating < avgRatingValueLimit)) {
    return true;
  }
  return false;
};

export const isValidRecoGroup = (group: string, groups: any) => {
  if (isValidObject(groups)) {
    return groups?.[group]?.includes('cav_brand_category') || false;
  }
  return false;
};

export const getPRData = (
  isDifferential: boolean,
  isPRV5Enabled: boolean,
  currentSku: Variant
) => {
  const { prData, cohortSaleTemplate, cohortSaleTemplateV2 } = currentSku || {};
  if (isDifferential) {
    if (isPRV5Enabled) {
      return cohortSaleTemplateV2;
    } else return cohortSaleTemplate;
  } else return prData;
};

export const checkIfIsMiniEnabled = (
  productCategories: string[],
  isMiniProductEnabled: boolean,
  primaryCategories: PrimaryCategories
): boolean => {
  if (!isMiniProductEnabled || !primaryCategories) return false;
  return (
    productCategories?.some((id) => id === primaryCategories?.l1?.id) ?? false
  );
};

export const checkIfFullHeightImgContainer = (
  productCategories: string[],
  isFullHeightContainer: boolean,
  primaryCategories: PrimaryCategories
): boolean => {
  if (!isFullHeightContainer || !primaryCategories || !productCategories) {
    return false;
  }
  const { l1, l2, l3 } = primaryCategories;
  const primaryCategoryIds = [l1?.id, l2?.id, l3?.id];
  return primaryCategoryIds.some((id) => productCategories.includes(id));
};

export const getCarouselStyle = (
  newSimilarEnabled: boolean,
  isFullHeightImg: boolean
): { height: string } => {
  if (isFullHeightImg) {
    return { height: '520px' };
  }
  if (newSimilarEnabled) {
    return { height: 'auto' };
  }
  return { height: '390px' };
};

export const useIsPDPage = () => {
  const { pathname } = useLocation();
  const pageType = getPageType(pathname);
  return pageType === PageTypes.PRODUCT;
};
export const useIsHLPPage = () => {
  const { pathname } = useLocation();
  const pageType = getPageType(pathname);
  return pageType === PageTypes.HLP;
};

export const getImageDimensions = (
  hasDynamicCard: boolean,
  cardWidth: number,
  cardHeight: number,
  imageResizeExp?: boolean
) => {
  const DEFAULT_IMAGE_SIZE = 200;
  const FULL_HEIGHT_DIMENSIONS = { width: 300, height: 400 };
  if (hasDynamicCard) {
    return { width: cardWidth, height: cardHeight };
  }
  return imageResizeExp
    ? FULL_HEIGHT_DIMENSIONS
    : { width: DEFAULT_IMAGE_SIZE, height: DEFAULT_IMAGE_SIZE };
};

export const isImageResizeEligible = (
  productCategories: string[],
  isImageResizeExpEnabled?: boolean,
  primaryCategories?: PrimaryCategories
): boolean => {
  if (!isImageResizeExpEnabled || !primaryCategories || !productCategories)
    return false;
  return (
    productCategories?.some((id) => id === primaryCategories?.l1?.id) ?? false
  );
};

export const isPlpVariantColorExpEligible = (
  productCategories: string[],
  plpVariantExp?: boolean,
  primaryCategories?: PrimaryCategories
): boolean => {
  if (!plpVariantExp || !primaryCategories || !productCategories) return false;
  return (
    productCategories?.some((id) => id === primaryCategories?.l1?.id) ?? false
  );
};

export const isPlpImageCarouselEligible = (
  productCategories: string[],
  plpImageCarouselExp?: boolean,
  primaryCategories?: PrimaryCategories
): boolean => {
  if (!plpImageCarouselExp || !primaryCategories || !productCategories)
    return false;
  return (
    productCategories.some((id) => id === primaryCategories?.l1?.id) ?? false
  );
};

export const isBuyNowEnabled = (
  isPdpBuyNowEnabled: boolean,
  primaryCategories: PrimaryCategories,
  allowedCategories: string[],
  productOfferPrice: number,
  discountedPriceThreshold: number
): boolean => {
  if (!isPdpBuyNowEnabled) return false;
  if (productOfferPrice <= discountedPriceThreshold) return false;
  if (!allowedCategories?.length) return true;

  return (
    allowedCategories.some((id) => id === primaryCategories?.l1?.id) ?? false
  );
};

export const isPlpVariantSelectorFashionEligible = (
  productCategories: string[],
  expEnabled?: boolean,
  primaryCategories?: PrimaryCategories
): boolean => {
  if (!expEnabled || !primaryCategories || !productCategories) return false;
  return (
    productCategories.some((id) => id === primaryCategories?.l1?.id) ?? false
  );
};

export const isPlpVariantSelectorGroomingEligible = (
  productCategories: string[],
  expEnabled?: boolean,
  primaryCategories?: PrimaryCategories
): boolean => {
  if (!expEnabled || !primaryCategories || !productCategories) return false;
  return (
    productCategories.some((id) => id === primaryCategories?.l1?.id) ?? false
  );
};

export const checkForProductNudges = (productNudges: ProductNudges) => {
  if (!productNudges) return false;
  const hasNonAttributeNudges = isValidArrayLength(
    productNudges.non_attribute_nudges
  );
  const hasAttributeNudges = isValidArrayLength(
    Object.keys(productNudges.attribute_nudges || {})
  );
  return hasNonAttributeNudges || hasAttributeNudges;
};
